import React, { useState, useEffect } from 'react';
import {
  Container, Typography, TextField, MenuItem, Button, Paper, Stack, Card, CardContent, CardActions,
  Checkbox, FormControlLabel, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Collapse, Box, Link, Chip, Avatar
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { ThemeProvider } from '@mui/material/styles';
import { useParams, useNavigate } from 'react-router-dom';
import baseTheme from '../../theme';
import { loadGoogleFont, buildSalonTheme } from '../../utils/salonTheme';
import SalonHeader from '../../components/SalonHeader';
import { salonSlugFromHost } from '../../utils/host';
import i18nInstance from '../../i18n';
import { api } from '../../api';
import { getCurrentUser } from '../../services/auth';
import { TermsAcceptField, TERMS_VERSION } from '../../components/Terms';
import { validateName, validatePhone, validatePassword, validateEmail, PHONE_RULE_KEY } from '../../utils/validation';
import { pickServiceName } from '../../utils/serviceName';
import { formatMoney } from '../../utils/money';
import PasswordRequirements from '../../components/PasswordRequirements';
import { useResendTimer, formatMMSS } from '../../hooks/useResendTimer';
import { mediaUrl } from '../../utils/media';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

const initialsOf = (name) => (name || '').trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase();

export default function BookByLink() {
  const { t, i18n } = useTranslation();
  const { remaining, start: startResend } = useResendTimer();
  const { hash, slug: paramSlug } = useParams(); // /book/:hash або /w/:slug
  // На піддомені салону (/book без параметра) slug беремо з хоста
  const slug = paramSlug || (!hash ? salonSlugFromHost() : null);
  const navigate = useNavigate();

  const [masters, setMasters] = useState([]);
  const [workspaceId, setWorkspaceId] = useState(null); // салон (для /w/:slug і лінків)
  const [workspaceName, setWorkspaceName] = useState('');
  const [salonTheme, setSalonTheme] = useState({}); // тема салону (колір/акцент/шрифт/лого)
  const [services, setServices] = useState([]);
  const [selectedMaster, setSelectedMaster] = useState('');
  const [selected, setSelected] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]); // часи для вибраної дати
  const [schedule, setSchedule] = useState(null); // { workdays[7], offDates[] }
  const [terms, setTerms] = useState(false); // прийняття правил
  // --- Промокод ---
  const [promoCode, setPromoCode] = useState('');
  const [promo, setPromo] = useState(null);      // { valid, code, discount, finalPrice, ... }
  const [promoErr, setPromoErr] = useState('');
  const [promoBusy, setPromoBusy] = useState(false);
  const [acceptedTermsVersion, setAcceptedTermsVersion] = useState(TERMS_VERSION); // версія правил салону
  const [time, setTime] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [guestErrors, setGuestErrors] = useState({}); // { name, phone }
  const [error, setError] = useState(''); // помилки форми (інлайн)
  const [loadError, setLoadError] = useState(''); // фатальна помилка завантаження (повний екран)
  const [loading, setLoading] = useState(false);
  const [loadingInitial, setLoadingInitial] = useState(!!hash || !!slug);
  const [currentUser, setCurrentUser] = useState(null); // залогінений клієнт

  // Хто зайшов — щоб не питати імʼя/телефон/код у зареєстрованого
  useEffect(() => {
    let ignore = false;
    getCurrentUser().then((u) => { if (!ignore) setCurrentUser(u); });
    return () => { ignore = true; };
  }, []);

  // --- OTP-верифікація ---
  const [otpOpen, setOtpOpen] = useState(false);
  const [otpStatus, setOtpStatus] = useState(''); // registered | guest | new
  const [otpDevCode, setOtpDevCode] = useState('');
  const [code, setCode] = useState('');
  const [otpError, setOtpError] = useState('');
  const [otpBusy, setOtpBusy] = useState(false);
  // опційне створення акаунта (для new/guest)
  const [wantAccount, setWantAccount] = useState(false);
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');

  // --- INIT: якщо є hash, отримати налаштування з бекенду ---
  useEffect(() => {
    async function fetchLinkData() {
      if (!hash) {
        setLoadingInitial(false);
        return;
      }
      try {
        // Дані лінка: { masterId, serviceId?, masterName, workspaceId?, workspaceName?, theme? }
        const linkData = await api.getBookingLink(hash);
        // Ім'я майстра беремо з лінка — без запиту до захищеного списку користувачів
        setMasters([{ id: linkData.masterId, name: linkData.masterName, avatarUrl: linkData.masterAvatar }]);
        setWorkspaceId(linkData.workspaceId || null);
        // Персональний лінк брендується темою салону — як і /w/:slug
        setSalonTheme(linkData.theme || {});
        setWorkspaceName(linkData.workspaceName || '');

        setSelectedMaster(linkData.masterId);
        if (linkData.serviceId) {
          setSelected([Number(linkData.serviceId)]);
        }

        const res = await api.getServices(linkData.masterId, linkData.workspaceId);
        setServices(res);
        // Єдина послуга без фіксації в лінку — вибираємо автоматично
        // (чекбокс у hash-режимі з 1 послугою disabled, інакше запис неможливий)
        if (!linkData.serviceId && res.length === 1) {
          setSelected([Number(res[0].id)]);
        }

        setLoadingInitial(false);
      } catch (e) {
        setLoadError(t('book.linkInvalid'));
        setLoadingInitial(false);
      }
    }
    fetchLinkData();
    // eslint-disable-next-line
  }, [hash]);

  // --- Режим салону /w/:slug: майстри лише цього салону ---
  useEffect(() => {
    if (hash || !slug) return;
    let ignore = false;
    async function fetchWorkspace() {
      try {
        const res = await api.getPublicWorkspace(slug);
        if (ignore) return;
        setMasters(res.masters || []);
        setWorkspaceId(res.workspace?.id || null);
        setWorkspaceName(res.workspace?.name || '');
        // Тема салону (колір/акцент/шрифт/лого)
        setSalonTheme(res.settings?.theme || {});
        // Мова за замовчуванням салону — лише якщо відвідувач ще не обрав свою
        const savedLang = (typeof localStorage !== 'undefined' && localStorage.getItem('lang')) || '';
        if (!savedLang && res.settings?.defaultLang) {
          i18nInstance.changeLanguage(res.settings.defaultLang);
        }
        if ((res.masters || []).length === 1) setSelectedMaster(res.masters[0].id);
        setLoadingInitial(false);
      } catch (e) {
        if (!ignore) {
          setLoadError(t('book.salonUnavailable'));
          setLoadingInitial(false);
        }
      }
    }
    fetchWorkspace();
    return () => { ignore = true; };
    // eslint-disable-next-line
  }, [slug, hash]);

  // --- Якщо ні hash, ні slug: стандартний флоу з усіма майстрами ---
  useEffect(() => {
    if (hash || slug) return;
    async function fetchData() {
      // Публічний список майстрів (лише id + ім'я)
      const mastersList = await api.getMasters();
      setMasters(mastersList);
      if (!selectedMaster && mastersList.length === 1) setSelectedMaster(mastersList[0].id);
    }
    fetchData();
    // eslint-disable-next-line
  }, [hash, slug]);

  // --- Завантаження послуг для майстра ---
  useEffect(() => {
    async function fetchServices() {
      if (selectedMaster && (!hash || services.length === 0)) {
        const res = await api.getServices(selectedMaster, workspaceId);
        setServices(res);
      }
    }
    fetchServices();
    // eslint-disable-next-line
  }, [selectedMaster]);

  // --- Розклад майстра: щоб вимкнути неробочі дні в календарі ---
  useEffect(() => {
    async function fetchSchedule() {
      if (!selectedMaster) { setSchedule(null); return; }
      try {
        setSchedule(await api.getPublicSchedule(selectedMaster, workspaceId));
      } catch {
        setSchedule(null);
      }
    }
    fetchSchedule();
  }, [selectedMaster]);

  // Скидаємо вибір дати/часу при зміні майстра або послуг
  useEffect(() => {
    setSelectedDate(null);
    setAvailableSlots([]);
    setTime('');
  }, [selectedMaster, selected]);

  // Вільні слоти для вибраної дати — з серверного availability (єдине джерело)
  useEffect(() => {
    async function loadSlots() {
      setAvailableSlots([]);
      setTime('');
      if (!selectedMaster || selected.length === 0 || !selectedDate) return;
      try {
        const res = await api.getAvailability(
          Number(selectedMaster),
          selectedDate.format('YYYY-MM-DD'),
          selected,
          workspaceId
        );
        setAvailableSlots(res.slots || []);
      } catch (e) {
        setAvailableSlots([]);
      }
    }
    loadSlots();
    // eslint-disable-next-line
  }, [selectedDate, selectedMaster, selected]);

  // Крок 1: валідація + (гість) надсилання коду / (залогінений) прямий запис
  const handleCreate = async () => {
    setError('');
    if (!selectedMaster || selected.length === 0 || !selectedDate || !time) {
      setError(t('book.selectServiceDateTime'));
      return;
    }
    if (!terms) {
      setError(t('book.mustAcceptTerms'));
      return;
    }

    // Залогінений користувач — бронюємо одразу під його акаунт, без OTP
    if (currentUser) {
      setLoading(true);
      try {
        await api.createBooking({
          masterId: Number(selectedMaster),
          date: selectedDate.format('YYYY-MM-DD'),
          time,
          services: selected,
          promoCode: promo?.valid ? promoCode.trim() : undefined,
          termsAccepted: true,
          termsVersion: acceptedTermsVersion,
          workspaceId: workspaceId || undefined,
        });
        navigate('/user/cabinet');
      } catch (e) {
        setError(e.message || t('book.createError'));
      } finally {
        setLoading(false);
      }
      return;
    }

    // Гість — потрібні валідні імʼя/телефон і підтвердження кодом
    const gErrs = {};
    const nErr = validateName(name);
    const pErr = validatePhone(phone);
    if (nErr) gErrs.name = nErr;
    if (pErr) gErrs.phone = pErr;
    setGuestErrors(gErrs);
    if (Object.keys(gErrs).length > 0) {
      setError(t('book.checkNamePhone'));
      return;
    }
    setLoading(true);
    try {
      const res = await api.requestOtp(phone);
      setOtpStatus(res.status || '');
      setOtpDevCode(res.devCode || '');
      setCode('');
      setOtpError('');
      setWantAccount(false);
      setRegEmail('');
      setRegPassword('');
      setOtpOpen(true);
      startResend(res.retryAfter || 120);
    } catch (e) {
      setError(e.message || t('book.otpSendFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Повторно надіслати код
  const handleResend = async () => {
    if (remaining > 0) return; // кулдаун ще триває
    setOtpError('');
    try {
      const res = await api.requestOtp(phone);
      setOtpDevCode(res.devCode || '');
      startResend(res.retryAfter || 120);
    } catch (e) {
      setOtpError(e.message || t('book.otpResendFailed'));
    }
  };

  // Крок 2: підтвердити код -> (опційно створити акаунт) -> створити запис
  const handleVerifyAndBook = async () => {
    setOtpError('');
    if (!code || code.length < 4) {
      setOtpError(t('auth.enterSmsCode'));
      return;
    }
    setOtpBusy(true);
    try {
      const v = await api.verifyOtp({ phone, code });

      // Для нового номера — за бажанням одразу створюємо акаунт (email опційний)
      if (wantAccount && otpStatus !== 'registered') {
        const pwErr = validatePassword(regPassword);
        const emErr = validateEmail(regEmail);
        if (pwErr || emErr) {
          setOtpError(t(pwErr || emErr));
          setOtpBusy(false);
          return;
        }
        await api.register({
          name,
          phone,
          email: regEmail || undefined,
          password: regPassword,
          phoneToken: v.phoneToken,
        });
      }

      await api.createBooking({
        masterId: Number(selectedMaster),
        date: selectedDate.format('YYYY-MM-DD'),
        time,
        services: selected,
        promoCode: promo?.valid ? promoCode.trim() : undefined,
        guest: { name, phone },
        phoneToken: v.phoneToken,
        termsAccepted: true,
        termsVersion: acceptedTermsVersion,
        workspaceId: workspaceId || undefined,
      });
      setOtpOpen(false);
      navigate('/book-success');
    } catch (e) {
      setOtpError(e.message || t('book.confirmFailed'));
    } finally {
      setOtpBusy(false);
    }
  };

  // Базова сума обраних послуг (для знижки й показу ціни)
  const baseSum = services
    .filter((sv) => selected.includes(sv.id))
    .reduce((acc, sv) => acc + Number(sv.price || 0), 0);

  // Перевірити промокод (без консюмингу; показує знижку)
  const applyPromo = async () => {
    if (!promoCode.trim() || !selectedMaster || selected.length === 0) return;
    setPromoBusy(true);
    setPromoErr('');
    try {
      const r = await api.validatePromo(Number(selectedMaster), promoCode.trim(), baseSum);
      if (r && r.valid) setPromo(r);
      else { setPromo(null); setPromoErr(t('book.promoInvalid')); }
    } catch (e) {
      setPromo(null);
      setPromoErr(t('book.promoInvalid'));
    }
    setPromoBusy(false);
  };

  // Тема салону: базова платформна тема з перевизначеними кольорами/шрифтом
  loadGoogleFont(salonTheme?.font);
  const muiTheme = buildSalonTheme(baseTheme, salonTheme);

  // --- Відображення ---
  if (loadingInitial) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress color="primary" sx={{ mb: 2 }} />
          <Typography>{t('common.loading')}</Typography>
        </Paper>
      </Container>
    );
  }
  if (loadError) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Alert severity="error">{loadError}</Alert>
        </Paper>
      </Container>
    );
  }

  // Повне брендування (шапка+фон) — і для /w/:slug, і для персонального лінка
  // /book/:hash, якщо лінк належить салону (майстер поділився — клієнт бачить
  // дизайн, налаштований у акаунті салону).
  const branded = Boolean(slug || (hash && workspaceId));

  return (
    <ThemeProvider theme={muiTheme}>
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{
        minHeight: branded ? '100vh' : 'auto',
        bgcolor: branded ? 'background.default' : 'transparent',
        // Фон сторінки запису: власне зображення салону, інакше — дефолтне фото
        ...(branded ? {
          // Фон: власне зображення салону (з «Дизайну»), інакше — нейтральний
          // градієнт платформи (жодних фото чужих салонів за замовчуванням)
          backgroundImage: salonTheme?.bgImageUrl
            ? `url(${mediaUrl(salonTheme.bgImageUrl)})`
            : 'linear-gradient(160deg, #f6f1e8 0%, #efe3d4 45%, #e9d9c6 100%)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
        } : {}),
      }}>
      {/* Брендована шапка салону (лого/назва/мова) */}
      {branded && <SalonHeader theme={salonTheme} name={workspaceName} />}
      <Container maxWidth="sm">
        <Paper sx={{ p: 3, mt: 3 }}>
          {/* Лого/назва в режимі персонального лінка (/book/:hash) — у режимі
              салону їх уже показує брендована шапка вгорі */}
          {!branded && salonTheme?.logoUrl && (
            <Box
              component="img"
              src={mediaUrl(salonTheme.logoUrl)}
              alt={workspaceName}
              sx={{ height: 48, mb: 1.5, display: 'block', objectFit: 'contain' }}
            />
          )}
          {!branded && workspaceName && (
            <Typography variant="overline" color="text.secondary" display="block">
              {workspaceName}
            </Typography>
          )}
          <Typography variant="h5" gutterBottom>{t('book.title')}</Typography>

          {currentUser ? (
            <Alert severity="info" sx={{ my: 2 }}>
              {t('book.loggedInAs')} <b>{currentUser.name}</b> ({currentUser.phone}). {t('book.bookingInCabinet')}
            </Alert>
          ) : (
            <>
              <TextField
                label={t('common.name')}
                fullWidth
                value={name}
                onChange={e => { setName(e.target.value); setGuestErrors(p => ({ ...p, name: '' })); }}
                error={!!guestErrors.name} helperText={guestErrors.name ? t(guestErrors.name) : ' '}
                sx={{ mt: 2 }}
              />
              <TextField
                label={t('common.phone')}
                fullWidth
                value={phone}
                placeholder="+48601234567"
                onChange={e => { setPhone(e.target.value); setGuestErrors(p => ({ ...p, phone: '' })); }}
                error={!!guestErrors.phone} helperText={t(guestErrors.phone || PHONE_RULE_KEY)}
                sx={{ mb: 1 }}
              />
            </>
          )}

          {!hash && (
            <TextField
              label={t('book.master')}
              select
              fullWidth
              value={selectedMaster}
              onChange={e => { setSelectedMaster(e.target.value); setSelected([]); }}
              sx={{ mb: 2 }}
            >
              {masters.map(m => (
                <MenuItem key={m.id} value={m.id}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar src={mediaUrl(m.avatarUrl)} sx={{ width: 28, height: 28, fontSize: 13 }}>
                      {initialsOf(m.name)}
                    </Avatar>
                    <span>{m.name}</span>
                  </Stack>
                </MenuItem>
              ))}
            </TextField>
          )}

          {/* Обраний майстер — кружок з фото */}
          {(() => {
            const m = masters.find(x => String(x.id) === String(selectedMaster));
            return m ? (
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <Avatar src={mediaUrl(m.avatarUrl)} sx={{ width: 56, height: 56, fontSize: 20 }}>
                  {initialsOf(m.name)}
                </Avatar>
                <Typography variant="h6">{m.name}</Typography>
              </Stack>
            ) : null;
          })()}

          {services.length > 0 && (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>{t('book.services')}</Typography>
              <Stack spacing={2} mb={2}>
                {services.map(s => (
                  <Card
                    key={s.id}
                    sx={{
                      backgroundColor: selected.includes(s.id) ? 'primary.main' : 'background.default',
                      color: selected.includes(s.id) ? 'primary.contrastText' : 'text.primary',
                      borderRadius: 2,
                      boxShadow: selected.includes(s.id) ? 6 : 1,
                      transition: '0.2s'
                    }}
                  >
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="bold">{pickServiceName(s, i18n.language)}</Typography>
                      <Typography variant="body2">{t('book.duration', { duration: s.duration })}</Typography>
                      <Typography variant="body2">{t('book.price', { price: s.price ? formatMoney(s.price) : '—' })}</Typography>
                    </CardContent>
                    <CardActions>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selected.includes(s.id)}
                            onChange={() => {
                              setSelected(prev =>
                                prev.includes(s.id)
                                  ? prev.filter(i => i !== s.id)
                                  : [...prev, s.id]);
                              // Знижка рахується від суми послуг — скидаємо застосований промокод
                              setPromo(null);
                              setPromoErr('');
                            }}
                            sx={{ color: 'inherit' }}
                          />
                        }
                        label={t('book.choose')}
                        disabled={!!(hash && services.length === 1)}
                      />
                    </CardActions>
                  </Card>
                ))}
              </Stack>
            </>
          )}

          {/* Промокод + підсумок ціни */}
          {selectedMaster && selected.length > 0 && (
            <Box sx={{ mb: 2, p: 2, borderRadius: 2, bgcolor: 'action.hover' }}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <TextField
                  label={t('book.promoLabel')}
                  value={promoCode}
                  onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromo(null); setPromoErr(''); }}
                  size="small"
                  fullWidth
                />
                <Button variant="outlined" onClick={applyPromo} disabled={promoBusy || !promoCode.trim()}
                  sx={{ whiteSpace: 'nowrap', mt: 0.25 }}>
                  {t('book.promoApply')}
                </Button>
              </Stack>
              {promo?.valid && (
                <Alert severity="success" sx={{ mt: 1, py: 0 }}>{t('book.promoApplied', { code: promo.code })}</Alert>
              )}
              {promoErr && <Alert severity="error" sx={{ mt: 1, py: 0 }}>{promoErr}</Alert>}
              <Stack spacing={0.5} sx={{ mt: 1.5 }}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">{t('book.priceSum')}</Typography>
                  <Typography variant="body2" sx={{
                    textDecoration: promo?.valid ? 'line-through' : 'none',
                    color: promo?.valid ? 'text.secondary' : 'text.primary',
                  }}>
                    {formatMoney(baseSum)}
                  </Typography>
                </Stack>
                {promo?.valid && (
                  <>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="success.main">{t('book.priceDiscount')}</Typography>
                      <Typography variant="body2" color="success.main">−{formatMoney(promo.discount)}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{t('book.priceFinal')}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatMoney(promo.finalPrice)}</Typography>
                    </Stack>
                  </>
                )}
              </Stack>
            </Box>
          )}

          {selectedMaster && selected.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>{t('book.selectDate')}</Typography>
              <Box sx={{
                display: 'flex', justifyContent: 'center',
                '& .MuiPickerStaticWrapper-content': { minWidth: 'auto' },
              }}>
                <StaticDatePicker
                  displayStaticWrapperAs="desktop"
                  value={selectedDate}
                  onChange={d => { setSelectedDate(d); setTime(''); }}
                  disablePast
                  shouldDisableDate={(d) => {
                    if (!schedule) return false;
                    const ds = d.format('YYYY-MM-DD');
                    // Дата з «відкритими» годинами доступна навіть у неробочий день тижня
                    if (schedule.openDates && schedule.openDates.includes(ds)) return false;
                    if (schedule.workdays && schedule.workdays[d.day()] === false) return true;
                    if (schedule.offDates && schedule.offDates.includes(ds)) return true;
                    return false;
                  }}
                />
              </Box>
            </Box>
          )}

          {selectedDate && availableSlots.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>{t('book.time')}</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {availableSlots.map(t => (
                  <Chip
                    key={t}
                    label={t}
                    clickable
                    onClick={() => setTime(t)}
                    sx={{
                      bgcolor: time === t ? 'primary.main' : 'transparent',
                      color: time === t ? 'primary.contrastText' : 'text.primary',
                      border: '1px solid',
                      borderColor: time === t ? 'primary.main' : 'divider',
                      fontWeight: 500,
                      '&:hover': {
                        bgcolor: time === t ? 'primary.main' : 'action.hover',
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {selectedDate && availableSlots.length === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>{t('book.noSlots')}</Alert>
          )}

          {selectedMaster && selected.length > 0 && time && (
            <Box sx={{ mb: 1 }}>
              <TermsAcceptField
                checked={terms}
                onChange={setTerms}
                workspaceId={workspaceId}
                onTerms={(d) => setAcceptedTermsVersion(String(d.version ?? TERMS_VERSION))}
              />
            </Box>
          )}

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Stack direction="row" spacing={2}>
            <Button variant="contained" onClick={handleCreate} fullWidth disabled={loading}>
              {loading ? t('book.sendingCode') : t('book.book')}
            </Button>
          </Stack>
        </Paper>

        {/* --- Діалог підтвердження номера кодом (OTP) --- */}
        <Dialog open={otpOpen} onClose={() => !otpBusy && setOtpOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>{t('book.confirmPhone')}</DialogTitle>
          <DialogContent>
            {otpStatus === 'registered' ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('book.registeredHint')}
              </Alert>
            ) : (
              <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">
                {t('book.codeSentPrefix')} <b>{phone}</b>. {t('book.codeSentSuffix')}
              </Typography>
            )}

            {otpDevCode && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {t('auth.devCode', { code: otpDevCode })}
              </Alert>
            )}

            <TextField
              label={t('auth.smsCode')}
              fullWidth
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputProps={{ inputMode: 'numeric' }}
              autoFocus
              sx={{ mb: 1 }}
            />

            <Link component="button" type="button" variant="body2" onClick={handleResend}
              disabled={otpBusy || remaining > 0}
              sx={{ color: remaining > 0 ? 'text.disabled' : undefined }}>
              {remaining > 0 ? t('auth.resendIn', { time: formatMMSS(remaining) }) : t('auth.resend')}
            </Link>

            {otpStatus !== 'registered' && (
              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={<Checkbox checked={wantAccount} onChange={e => setWantAccount(e.target.checked)} />}
                  label={t('book.createAccount')}
                />
                <Collapse in={wantAccount}>
                  <Stack spacing={1.5} sx={{ mt: 1 }}>
                    <TextField label={t('common.emailOptional')} type="email" fullWidth value={regEmail}
                      onChange={e => setRegEmail(e.target.value)} />
                    <TextField label={t('common.password')} type="password" fullWidth value={regPassword}
                      onChange={e => setRegPassword(e.target.value)} />
                    <PasswordRequirements value={regPassword} sx={{ px: 0.5 }} />
                  </Stack>
                </Collapse>
              </Box>
            )}

            {otpError && <Alert severity="error" sx={{ mt: 2 }}>{otpError}</Alert>}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setOtpOpen(false)} disabled={otpBusy}>{t('common.cancel')}</Button>
            <Button variant="contained" onClick={handleVerifyAndBook} disabled={otpBusy}>
              {otpBusy ? t('book.confirming') : t('book.confirmAndBook')}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
      </Box>
    </LocalizationProvider>
    </ThemeProvider>
  );
}
